// src/pages/GamePage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';

import Header from '../components/Header';
import CommunityBestDisplay from '../components/CommunityBestDisplay';
import PyramidDisplay from '../components/PyramidDisplay';
import InputRow from '../components/InputRow';
import Keyboard from '../components/Keyboard';
import StatsModal from '../components/StatsModal';
import GameStatusMessage from '../components/GameStatusMessage';
import HelpModal from '../components/HelpModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ArchiveIntroModal from '../components/ArchiveIntroModal';
import CentumthPromo from '../components/CentumthPromo';
import { useStats } from '../hooks/useStats';
import { getMedalThresholds } from '../utils/medalUtils';

const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
};

const getYesterdayString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
};

const GamePage = () => {
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');

  // Determine the game date: query param OR today
  const gameDate = dateParam || getTodayString();
  const isArchiveGame = gameDate !== getTodayString();

  // State management
  const [wordChain, setWordChain] = useState([]);
  const [typedWord, setTypedWord] = useState('');
  const [dictionary, setDictionary] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameError, setGameError] = useState(''); 
  const [communityBest, setCommunityBest] = useState({ length: 0, word: '', chain: [] });
  const [revealedData, setRevealedData] = useState({ word: null, path: null });
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRevealWordConfirmOpen, setIsRevealWordConfirmOpen] = useState(false);
  const [isRevealingWord, setIsRevealingWord] = useState(false);
  const [isRevealingPath, setIsRevealingPath] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const { stats, recordPlay, recordWin, recordLoss, updateTodaysBest, freezeTodaysBest, lockMedals } = useStats();
  
  const hasCelebratedRef = React.useRef(false);

  // Check if medals are currently locked for this date
  const areMedalsLocked = useMemo(() => {
      const result = stats.dailyResults[gameDate];
      // For archive games, lockout from 'today' doesn't matter, but lockout from that specific date does.
      return result?.medalsLocked || (isArchiveGame ? false : isLockedOut);
  }, [stats, isLockedOut, gameDate, isArchiveGame]);
  
  // Calculate current tier for keyboard coloring
  const currentTier = useMemo(() => {
      const bestLen = communityBest.length;
      if (!bestLen || wordChain.length === 0) return 'default';
      const currentLen = wordChain[wordChain.length - 1].length;
      
      const bronze = Math.max(1, Math.round(bestLen * 0.4));
      const silver = Math.max(1, Math.round(bestLen * 0.6));
      const gold = Math.max(1, Math.round(bestLen * 0.8));
      
      if (currentLen >= bestLen) return 'platinum';
      if (currentLen >= gold) return 'gold';
      if (currentLen >= silver) return 'silver';
      if (currentLen >= bronze) return 'bronze';
      return 'default';
  }, [communityBest.length, wordChain]);


  const letterStates = useMemo(() => {
    const states = {};
    const starterWord = wordChain.length > 0 ? wordChain[0] : '';
    const allChainLetters = wordChain.join('');

    // Helper to count letters in a single word
    const getWordCounts = (word) => {
        const counts = {};
        for (const letter of word) {
            counts[letter] = (counts[letter] || 0) + 1;
        }
        return counts;
    };

    // 1. Calculate the max count for each letter across the entire word chain
    const maxChainCounts = {};
    for (const word of wordChain) {
        const wordCounts = getWordCounts(word);
        for (const letter in wordCounts) {
            maxChainCounts[letter] = Math.max(maxChainCounts[letter] || 0, wordCounts[letter]);
        }
    }

    // 2. Calculate counts for the currently typed word for instant feedback
    const typedWordCounts = getWordCounts(typedWord);

    // 3. Determine final state and count for each letter
    for (const letter of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
        let state = 'unused';
        // State based on presence in chain
        if (allChainLetters.includes(letter)) {
            if (starterWord.includes(letter)) {
                state = 'starter';
            } else {
                state = 'used';
            }
        }
        // Typing state takes precedence
        if (typedWord.includes(letter)) {
            state = 'typing';
        }

        // Final count is the max of chain history and current typing
        const finalCount = Math.max(
            maxChainCounts[letter] || 0,
            typedWordCounts[letter] || 0
        );

        states[letter] = {
            state: state,
            count: finalCount
        };
    }

    return states;
  }, [wordChain, typedWord]);

  // Fetch dictionary on component mount
  useEffect(() => {
    const fetchGameData = async () => {
        setIsLoading(true);

        // Start all fetches in parallel
        const dictionaryPromise = fetch('/dictionary.txt').then(res => res.text());
        
        const puzzlePromise = supabase
            .from('daily_puzzles')
            .select('starter_word')
            .eq('puzzle_date', gameDate)
            .single();

        const bestPromise = supabase
            .from('community_bests')
            .select('longest_word_length')
            .eq('puzzle_date', gameDate)
            .single();

        try {
            // Await all promises
            const [dictText, puzzleResult, bestResult] = await Promise.all([
                dictionaryPromise,
                puzzlePromise,
                bestPromise
            ]);

            // Process Dictionary
            const words = dictText.split('\n').map(word => word.trim().replace(/"/g, '').toUpperCase());
            setDictionary(new Set(words));
            console.log("Dictionary loaded.");

            // Process Puzzle
            const { data: puzzleData, error: puzzleError } = puzzleResult;
            if (puzzleError || !puzzleData) {
                console.error("Error fetching puzzle:", puzzleError);
                throw new Error("Could not load puzzle.");
            }

            // Process Community Best
            const { data: bestData } = bestResult;
            if (bestData) {
                setCommunityBest({ length: bestData.longest_word_length });
            }

            // Process Game State (Lockout & Archive)
            // Check for lockout status
            if (!isArchiveGame) {
                const today = getTodayString();
                const lockoutKey = `wordamid-lockout-${today}`;
                if (localStorage.getItem(lockoutKey)) {
                    setIsLockedOut(true);
                }
            } else {
                setIsLockedOut(false);
            }

            // Load saved progress from local storage OR stats if archive
            let initialChain = [puzzleData.starter_word];
            
            // Try local storage first for ongoing games
            try {
                const savedProgress = localStorage.getItem('wordamid-progress');
                if (savedProgress) {
                    const { date, chain } = JSON.parse(savedProgress);
                    // Check if the saved progress matches the requested game date
                    if (date === gameDate && chain[0] === puzzleData.starter_word) {
                        initialChain = chain;
                    }
                }
            } catch (e) {
                console.error("Failed to parse saved progress:", e);
            }

            // If playing archive, check existing stats to resume/show previous play
            if (isArchiveGame) {
                 const result = stats.dailyResults[gameDate];
                 if (result && result.wordChain) {
                     initialChain = result.wordChain;
                 }
            }
            
            setWordChain(initialChain);
            setIsLoading(false);

        } catch (err) {
            console.error("Error loading game data:", err);
            setError("Could not load game data. Please refresh.");
            setIsLoading(false);
        }
    };

    fetchGameData();
}, [gameDate, isArchiveGame, stats.dailyResults]);

  // Get the last word in the chain, which is the current word to build upon.
  const currentWord = wordChain.length > 0 ? wordChain[wordChain.length - 1] : '';

  const validateWord = (newWord, currentWord, dictionary) => {
    // 1. Check length first. This is a simple, fast check.
    if (newWord.length !== currentWord.length + 1) {
        // This case should theoretically not be hit if the input length is controlled, but it's good practice.
        return `Word must be ${currentWord.length + 1} letters long.`;
    }

    // 2. Check if the word exists in the dictionary.
    if (!dictionary.has(newWord)) {
        return `"${newWord}" is not a valid word.`;
    }

    // 3. Check the letter composition. This is the most complex rule.
    const currentLetters = [...currentWord];
    const newLetters = [...newWord];

    // For each letter in the old word, try to find and "remove" it from the new word.
    for (const letter of currentLetters) {
        const index = newLetters.indexOf(letter);
        if (index > -1) {
            // Found the letter, remove it from our copy of the new word's letters.
            newLetters.splice(index, 1);
        } else {
            // If a letter from the old word is missing in the new one, it's invalid.
            return `The letter "${letter}" from "${currentWord}" is missing.`;
        }
    }

    // After removing all the letters from the previous word, we should be left with exactly one new letter.
    if (newLetters.length === 1) {
        return null; // All checks passed, the word is valid!
    } else if (newLetters.length === 0) {
        // This would happen if the new word is an anagram of the old one, but the length check should prevent this.
        return "Must add one new letter.";
    } else {
        // This happens if more than one letter was changed or added (e.g., REL -> RAPID).
        return "Must use all letters from the previous word plus exactly one new letter.";
    }
};


const submitNewBest = async (chain) => {
    // Only submit for TODAY's puzzle
    if (isArchiveGame) return;

    // GUARD CLAUSE: If locked out, do nothing.
    if (isLockedOut) {
        console.log("Submission blocked: Player is locked out for the day.");
        return;
    }
    
    // GUARD: If medals are locked (Word Revealed), also block submission.
    if (areMedalsLocked) {
        console.log("Submission blocked: Medals are locked.");
        return;
    }

    // Only submit if the new word is longer than the current community best
    if (chain.length > 0 && chain[chain.length-1].length > communityBest.length) {
        console.log("New record! Submitting to server...");
        try {
            const response = await supabase.functions.invoke('submit-new-best', {
                body: {
                    puzzle_date: gameDate,
                    word_chain: chain,
                },
            });
            if (response.error) {
                throw response.error;
            }
            console.log("Successfully submitted new best:", response.data);
            // Optionally, you can update the community best in the state immediately
            setCommunityBest(prev => ({ ...prev, length: chain[chain.length-1].length }));
        } catch (error) {
            console.error("Error submitting new best score:", error);
            // Don't show this error to the user, as the game can continue.
            // It's a non-critical background task.
        }
    }
};

const handleRevealWordClick = () => {
     // Check if locked out or if medals already locked? 
     // The prompt is specifically to warn about losing ability to earn medals.
     // If medals are ALREADY locked (e.g. they revealed before), maybe no need for modal or different modal?
     // For now, always confirm.
     setIsRevealWordConfirmOpen(true);
};

const handleConfirmRevealWord = async () => {
    setIsRevealingWord(true);
    
    // Lock medals AND submit (which are now effectively linked)
    lockMedals(gameDate);
    
    try {
        const { data, error } = await supabase.functions.invoke('get-community-best-details', {
            body: { puzzle_date: gameDate },
        });
        if (error) throw error;
        setRevealedData({ word: data.longest_word_example, path: null }); // Only reveal the word first
    } catch (err) {
        console.error("Error revealing word:", err);
        setGameError("Could not fetch the community best word.");
    } finally {
        setIsRevealingWord(false);
        setIsRevealWordConfirmOpen(false); // Close modal
    }
};

const handleRevealPath = () => {
    // If they aren't locked out, ask for confirmation.
    if (areMedalsLocked) {
        // Skip confirmation lockout warning since already locked
        handleConfirmRevealAndLockout(); 
    } else if (!isLockedOut) {
        setIsConfirmModalOpen(true);
    }
};

// 2. ADD this NEW function to handle the confirmation logic:
const handleConfirmRevealAndLockout = async () => {
    setIsRevealingPath(true);
    // Reveal the path (this is the old logic from handleRevealPath)
    try {
        const { data, error } = await supabase.functions.invoke('get-community-best-details', {
            body: { puzzle_date: gameDate },
        });
        if (error) throw error;
        setRevealedData({ word: data.longest_word_example, path: data.word_chain });
    } catch (err) {
        console.error("Error revealing path:", err);
        setGameError("Could not fetch the community best path.");
    }

    // Lock the player out (Game Over state) - ONLY for today's game
    if (!isArchiveGame) {
        setIsLockedOut(true);
        const lockoutKey = `wordamid-lockout-${gameDate}`;
        localStorage.setItem(lockoutKey, 'true');
    }
    
    const playerBestLength = wordChain[wordChain.length - 1].length;
    const playerBestWord = wordChain[wordChain.length - 1];
    
    if (!isArchiveGame) freezeTodaysBest(gameDate);

    // Record loss logic (or just update stats for archive)
    recordLoss(playerBestLength, playerBestWord, wordChain, gameDate, communityBest.length);
    
    // Close the confirmation modal
    setIsConfirmModalOpen(false);
    setIsRevealingPath(false);
};

// const handleConfirmReveal = () => {
//     setIsConfirmModalOpen(true);
// };

  const triggerConfetti = () => {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(function() {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
              return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          confetti({
              ...defaults, 
              particleCount,
              origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
          });
          confetti({
              ...defaults, 
              particleCount,
              origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
          });
      }, 250);
  };

  const handleKeyPress = useCallback((key) => {
      setGameError(''); // Clear previous error on new input

      if (key === 'ENTER') {
          if (!currentWord || typedWord.length !== currentWord.length + 1) return;

          const validationError = validateWord(typedWord, currentWord, dictionary);
          if (validationError) {
              setGameError(validationError);
              setIsShaking(true);
              setTimeout(() => setIsShaking(false), 600); // Duration of the animation
          } else {
              // Success!
              const newChain = [...wordChain, typedWord];
              const newLength = newChain[newChain.length - 1].length;
              const newBestWord = newChain[newChain.length - 1];

              // First play of the day?
              if (newChain.length === 2) {
                  recordPlay(newLength, newBestWord, wordChain[0], newChain, gameDate, communityBest.length);
              } else {
                  // It's a subsequent play, so update the best length for today
                  updateTodaysBest(newLength, newBestWord, newChain, gameDate, communityBest.length);
              }

              // Check for a win
              const thresholds = getMedalThresholds(communityBest.length);
              if (newLength >= thresholds.platinum && !hasCelebratedRef.current) {
                  hasCelebratedRef.current = true;
                  triggerConfetti();
              }

              if (newLength >= communityBest.length && communityBest.length > 0) {
                  recordWin(newLength, newBestWord, newChain, gameDate, communityBest.length);
              }

              setWordChain(newChain);
              setTypedWord('');
              setShowSuccessAnimation(true);
              
              // Call the submission function
              submitNewBest(newChain);

              // Save to localStorage ONLY if it's today's game
              if (!isArchiveGame) {
                  try {
                      const progress = {
                          date: getTodayString(),
                          chain: newChain
                      };
                      localStorage.setItem('wordamid-progress', JSON.stringify(progress));
                  } catch (error) {
                      console.error("Failed to save progress:", error);
                  }
              }
          }
          return;
      }

      if (key === 'DELETE' || key === 'BACKSPACE') {
          setTypedWord(prev => prev.slice(0, -1));
          return;
      }

      // Check if it's a letter
      if (/^[A-Z]$/.test(key.toUpperCase())) {
          if (!currentWord || typedWord.length < currentWord.length + 1) {
              setTypedWord(prev => prev + key.toUpperCase());
          }
      }
  }, [typedWord, currentWord, wordChain, dictionary, communityBest.length, recordPlay, recordWin, updateTodaysBest, isLockedOut, areMedalsLocked, gameDate, isArchiveGame]);

  const handleUndo = () => {
    if (wordChain.length > 1) { // Can't undo the starter word
        const newChain = wordChain.slice(0, -1);
        setWordChain(newChain);
        setTypedWord(''); // Clear any typed text
        setGameError(''); // Clear any errors

        // Update localStorage
        if (!isArchiveGame) {
            try {
                const progress = {
                    date: getTodayString(),
                    chain: newChain
                };
                localStorage.setItem('wordamid-progress', JSON.stringify(progress));
            } catch (error) {
                console.error("Failed to save progress after undo:", error);
            }
        }
    }
  };

  // Add listener for physical keyboard
  useEffect(() => {
      const handleKeyDown = (event) => {
          let key = event.key.toUpperCase();
          if (key === 'BACKSPACE') key = 'DELETE'; // Normalize
          if (key === 'ENTER' || key === 'DELETE' || (key.length === 1 && key >= 'A' && key <= 'Z')) {
              handleKeyPress(key);
          }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
          window.removeEventListener('keydown', handleKeyDown);
      };
  }, [handleKeyPress]);

  // Reset success animation after 500ms
  useEffect(() => {
    if (showSuccessAnimation) {
        const timer = setTimeout(() => {
            setShowSuccessAnimation(false);
        }, 500);
        return () => clearTimeout(timer);
    }
  }, [showSuccessAnimation]);

  if (isLoading) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground">
            <div className="animate-bounce mb-6">
                 <img src="/wordamid-logo- final.png" alt="Wordamid" className="h-20 w-auto" />
            </div>
            <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-accent dark:border-t-dark-accent rounded-full animate-spin"></div>
            <p className="mt-4 text-sm font-semibold tracking-widest uppercase text-gray-500 animate-pulse">Loading Game...</p>
        </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center min-h-screen bg-background dark:bg-dark-background text-red-500 dark:text-red-400 text-2xl">{error}</div>;
  }

  // Determine the game status
  let gameStatus = 'playing';
  const todayResult = stats.dailyResults[gameDate];
  if (todayResult) {
      if (todayResult.status === 'won') {
          gameStatus = 'won';
      } else if (todayResult.status === 'lost') {
          gameStatus = 'lost';
      }
  }
  const playerBestLength = wordChain.length > 0 ? wordChain[wordChain.length - 1].length : 0;

  return (
    <div className="flex flex-col h-screen items-center">
      <Header 
          onStatsClick={() => setIsStatsModalOpen(true)} 
          onHelpClick={() => setIsHelpModalOpen(true)} 
          onArchiveClick={() => setIsArchiveModalOpen(true)}
      />
      <main className="flex-grow flex flex-col p-4 w-full max-w-md lg:max-w-2xl px-2 sm:px-4 overflow-y-auto">
        <CommunityBestDisplay 
            communityBestLength={communityBest.length}
            revealedData={revealedData}
            onRevealWord={handleRevealWordClick} // Updated handler
            onRevealPath={handleRevealPath}      // Updated handler (direct call or modal depending on state)
            isRevealingWord={isRevealingWord}
            playerBestLength={playerBestLength}
        />
        <GameStatusMessage 
            gameStatus={gameStatus}
            playerLength={playerBestLength} 
            communityBestLength={communityBest.length}
            areMedalsLocked={areMedalsLocked}
        />

        {/* --- NEW STRUCTURE START --- */}
        <div className="flex-grow flex flex-col justify-center">
            <PyramidDisplay 
                wordChain={wordChain} 
                showSuccessAnimation={showSuccessAnimation} 
                communityBestLength={communityBest.length}
            />
            
            {/* InputRow is now part of the pyramid */}
            <InputRow 
                typedWord={typedWord} 
                expectedLength={currentWord ? currentWord.length + 1 : 0} 
                isShaking={isShaking}
            />
            <p className="text-center h-6 text-red-500 mt-2">{gameError}</p>
        </div>
        {/* --- NEW STRUCTURE END --- */}

      </main>
        <div className="w-full max-w-md lg:max-w-2xl px-2 sm:px-4 pb-2">
            <Keyboard 
                onKeyPress={handleKeyPress} 
                onUndo={handleUndo} 
                letterStates={letterStates} 
                currentTier={currentTier}
            />
        </div>
      <StatsModal 
        isOpen={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        stats={stats}
        onArchiveClick={() => {
            setIsStatsModalOpen(false);
            setIsArchiveModalOpen(true);
        }}
      />
      <HelpModal 
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
      <ArchiveIntroModal 
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
      />
      {/* Existing Reveal Path Confirmation - Only shown if not already locked */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmRevealAndLockout}
        title="Reveal Path?"
        isConfirming={isRevealingPath}
    >
        <p>Revealing the full path will lock you out from submitting a new Community Best for the rest of the day.</p>
        <p className="mt-2">You can still play for fun!</p>
    </ConfirmationModal>

    {/* NEW Reveal Word Confirmation */}
    <ConfirmationModal
        isOpen={isRevealWordConfirmOpen}
        onClose={() => setIsRevealWordConfirmOpen(false)}
        onConfirm={handleConfirmRevealWord}
        title="Reveal Word?"
        isConfirming={isRevealingWord}
    >
        <p className="text-red-500 font-bold mb-2">Warning!</p>
        <p>If you reveal the word, you will NOT be able to earn any more medals or submit a Community Best for today.</p>
        <p className="mt-2 text-sm">Are you sure you want to proceed?</p>
    </ConfirmationModal>
    <CentumthPromo />
    </div>
  );
};

export default GamePage;
